/**
 * Saisie d'un montant par destination et par période — les coûts, les budgets prévisionnels.
 *
 * ## Un seul écran pour les deux
 *
 * Coûts et budgets se saisissent exactement du même geste : on choisit une destination, puis on
 * porte un montant en face d'une période. Seuls changent les mots et la route d'enregistrement.
 * Deux composants auraient divergé au premier ajustement — un groupement de destinations revu
 * d'un côté, pas de l'autre — et l'utilisateur aurait trouvé deux ergonomies là où il fait deux
 * fois la même chose.
 *
 * ## Ce que les coûts viennent poser
 *
 * Un canal gratuit — SEO, emailing, réseaux sociaux — n'a aucun coût de régie : son COS et son
 * CPA n'existaient donc pas, alors que le travail qui l'alimente en a un.
 *
 * Une régie payante, elle, en a déjà un, mais l'API ne rend que l'achat d'espace. Le coût fixe
 * qu'elle facture par ailleurs — abonnement, frais de gestion, minimum mensuel — se déclare
 * ici et **s'ajoute** au coût remonté, sans le remplacer.
 *
 * **Une destination par ligne, et non une colonne par destination.** Les coûts ne sont pas en
 * nombre fixe : le SEO d'abord, une prestation ensuite, peut-être une régie. Une colonne par
 * cas élargirait la grille sans fin et imposerait une migration à chaque ajout. On choisit donc
 * la destination au-dessus de la grille, et la grille ne porte qu'un montant.
 *
 * Tout le reste — granularités, périodes, règle du vide contre le zéro, avertissement sur les
 * périodes plus fines déjà saisies — vient de `GrilleDeSaisie`, partagée avec le back-office et
 * les objectifs. Les mêmes gestes, exactement.
 */

import React, { useMemo, useState } from 'react';
import GrilleDeSaisie from './GrilleDeSaisie';

const LIBELLES_FAMILLE = {
  gratuit: 'Canaux sans régie',
  regie: 'Régies',
  global: 'Hors canal'
};

/**
 * @param {Array}  lignes          les montants déjà saisis, toutes destinations confondues
 * @param {Array}  cibles          le catalogue des destinations, **envoyé par le serveur**
 * @param {string} titreDuChamp    l'en-tête de la colonne — « Coût (€) », « Budget (€) »
 * @param {string} titreDuSelect   l'étiquette au-dessus du menu des destinations
 * @param {Function} aideDeLaCible le texte d'aide, construit à partir de la destination choisie
 */
function OngletMontantsParCible({
  lignes, cibles, titreDuChamp, titreDuSelect, aideDeLaCible,
  onEnregistrer, enregistrement, erreur
}) {
  /**
   * Rien n'est choisi au départ, et la grille reste fermée jusqu'à ce qu'on choisisse.
   *
   * La première destination était présélectionnée : la grille s'ouvrait donc **déjà remplie**
   * des montants du SEO, sans que rien n'ait été demandé. Quelqu'un qui venait saisir des frais
   * de régie les portait en face d'un intitulé qu'il n'avait pas lu, et l'erreur ne se voyait
   * qu'à la relecture — la destination ne figurant nulle part dans la grille.
   */
  const [cible, setCible] = useState('');

  const champs = useMemo(
    () => [{ cle: 'montant', titre: titreDuChamp, entier: false }],
    [titreDuChamp]
  );

  const cibleActuelle = useMemo(
    () => (cibles || []).find(c => c.cle === cible) || null,
    [cibles, cible]
  );

  /**
   * La grille ne voit que les lignes de la destination choisie.
   *
   * Elle indexe ses lignes par date de début : lui passer toutes les destinations ferait
   * apparaître le montant du SEO en face de la prestation, puisque les deux partagent la période.
   */
  const lignesDeLaCible = useMemo(
    () => (lignes || []).filter(l => l.cible === cible),
    [lignes, cible]
  );

  /** Les destinations, groupées par famille : quinze entrées à plat ne se parcourent pas. */
  const parFamille = useMemo(() => {
    const groupes = new Map();
    for (const c of cibles || []) {
      if (!groupes.has(c.famille)) groupes.set(c.famille, []);
      groupes.get(c.famille).push(c);
    }
    return [...groupes.entries()];
  }, [cibles]);

  // La destination est réattachée ici : la grille ne la connaît pas, et n'a pas à la connaître.
  const enregistrer = (modifiees, onSucces) => onEnregistrer(
    modifiees.map(l => ({ ...l, cible })),
    onSucces
  );

  if (!cibles || cibles.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
        Aucune destination disponible pour cette propriété.
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        marginBottom: '14px'
      }}>
        <label htmlFor="cible-montant" style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 500 }}>
          {titreDuSelect}
        </label>
        <select
          id="cible-montant"
          value={cible}
          onChange={(e) => setCible(e.target.value)}
          style={{
            padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db',
            fontSize: '0.8125rem', background: '#fff', cursor: 'pointer', minWidth: '220px'
          }}
        >
          {/* L'invite est une option à part entière, et non un `placeholder` : un menu déroulant
              n'en a pas, et sans elle le premier choix serait fait à la place de l'utilisateur. */}
          <option value="">Sélectionner une destination…</option>
          {parFamille.map(([famille, entrees]) => (
            <optgroup key={famille} label={LIBELLES_FAMILLE[famille] || famille}>
              {entrees.map(c => <option key={c.cle} value={c.cle}>{c.titre}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {cible ? (
        <>
          <p style={{ margin: '0 0 14px', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5 }}>
            {aideDeLaCible(cibleActuelle)}
          </p>

          <GrilleDeSaisie
            saisies={lignesDeLaCible}
            champs={champs}
            onEnregistrer={enregistrer}
            enregistrement={enregistrement}
            erreur={erreur}
          />
        </>
      ) : (
        <div style={{
          padding: '32px', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem',
          border: '1px dashed #e5e7eb', borderRadius: '10px', lineHeight: 1.5
        }}>
          Choisissez une destination ci-dessus pour afficher la grille de saisie.
          <br />
          <span style={{ fontSize: '0.8125rem' }}>
            Chaque destination a ses propres montants : ils ne se mélangent pas.
          </span>
        </div>
      )}
    </div>
  );
}

export default OngletMontantsParCible;
